import downloadFile from '../../src/modules/shared/procedures/download-file';
import apiPaths from '../api-paths';

/**
 * Przy `responseType: 'blob'` ciało odpowiedzi błędnej też jest Blobem, więc standardowa obsługa
 * (`notifyCaughtError` → `notifyErrorResponse`) nie znalazłaby w nim `message` i pokazałaby
 * użytkownikowi „[object Blob]". Zamieniamy więc ciało na czytelny komunikat, zanim błąd poleci dalej.
 */
async function withReadableBlobError(promise) {
  try {
    return await promise;
  } catch (error) {
    const data = error?.response?.data;

    if (!data || typeof data.text !== 'function') {
      throw error;
    }

    const body = await data.text();
    let message = 'Nie udało się pobrać pliku.';

    try {
      const parsed = JSON.parse(body);
      message = parsed.message || parsed.error || message;
    } catch (parseError) {
      // Odpowiedź nie jest JSON-em (np. strona błędu) — zostaje komunikat domyślny.
    }

    error.response.data = { exception: 'Pobieranie dokumentu', message };

    throw error;
  }
}

/**
 * Repozytorium Dokumenty (PUB-1649).
 *
 * Pobranie pliku idzie przez endpoint dokumentu (`/documents/{id}/download`), a nie przez
 * `/files/download/{id}` — granicą autoryzacji jest dokument, nie sam plik.
 */
export default {
  index(params = {}) {
    return axios.get(`${ apiPaths.API }/documents`, { params });
  },
  show(id) {
    return axios.get(`${ apiPaths.API }/documents/${ id }`);
  },
  /**
   * Pobranie idzie przez autoryzowanego klienta HTTP aplikacji, a nie przez otwarcie adresu
   * w nowej karcie: tamta droga gubi nagłówki auth/tenant i kończy się przekierowaniem na
   * nieistniejącą trasę [login]. Binarkę odbieramy jako blob i zapisujemy wspólną procedurą.
   */
  download(id, filename) {
    return withReadableBlobError(downloadFile({
      action: axios.get(`${ apiPaths.API }/documents/${ id }/download`, { responseType: 'blob' }),
      filename,
    }));
  },
  downloadPage(id, pageId, filename) {
    return withReadableBlobError(downloadFile({
      action: axios.get(`${ apiPaths.API }/documents/${ id }/pages/${ pageId }/download`, { responseType: 'blob' }),
      filename,
    }));
  },
  store(data) {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value === null || value === undefined || key === 'relations' || key === 'files') {
        return;
      }

      formData.append(key, value);
    });

    // Kolejne strony tego samego dokumentu. Backend tworzy z nich JEDEN Document.
    (data.files || []).forEach((file, index) => {
      formData.append(`files[${ index }]`, file);
    });

    (data.relations || []).forEach((relation, index) => {
      formData.append(`relations[${ index }][relation_type]`, relation.relation_type);
      formData.append(`relations[${ index }][relation_object_id]`, relation.relation_object_id);
    });

    return axios.post(`${ apiPaths.API }/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /**
   * Strony istniejącego dokumentu — dodanie i usunięcie bez tworzenia dokumentu od nowa.
   */
  addPage(id, file) {
    const formData = new FormData();
    formData.append('file', file);

    return axios.post(`${ apiPaths.API }/documents/${ id }/pages`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  removePage(id, pageId) {
    return axios.delete(`${ apiPaths.API }/documents/${ id }/pages/${ pageId }`);
  },
  update(id, data) {
    return axios.put(`${ apiPaths.API }/documents/${ id }`, data);
  },
  delete(id) {
    return axios.delete(`${ apiPaths.API }/documents/${ id }`);
  },
  addRelation(id, relation) {
    return axios.post(`${ apiPaths.API }/documents/${ id }/relations`, relation);
  },
  removeRelation(id, relationId) {
    return axios.delete(`${ apiPaths.API }/documents/${ id }/relations/${ relationId }`);
  },
  /**
   * Archiwum → Dokumenty. Ten sam kształt odpowiedzi co lista aktywnych.
   */
  archived(params = {}) {
    return axios.get(`${ apiPaths.API }/documents/archived`, { params });
  },
  archive(id) {
    return axios.post(`${ apiPaths.API }/documents/${ id }/archive`);
  },
  unarchive(id) {
    return axios.post(`${ apiPaths.API }/documents/${ id }/unarchive`);
  },
  /**
   * Katalog do WYBORU w formularzach — wyłącznie kategorie aktywne.
   */
  categories() {
    return axios.get(`${ apiPaths.API }/document-categories`);
  },
  /**
   * Katalog do ZARZĄDZANIA (Admin → Szablony) — razem z kategoriami nieaktywnymi, inaczej
   * dezaktywowanej kategorii nie dałoby się już zobaczyć ani włączyć z powrotem.
   */
  manageableCategories() {
    return axios.get(`${ apiPaths.API }/document-categories`, { params: { manage: 1 } });
  },
  storeCategory(data) {
    return axios.post(`${ apiPaths.API }/document-categories`, data);
  },
  updateCategory(id, data) {
    return axios.put(`${ apiPaths.API }/document-categories/${ id }`, data);
  },
  /**
   * Dokumenty (umowy) powiązane z fakturą. `relationType` rozróżnia oba źródła faktur:
   * `uploaded_file` dla „Moje pliki → Lista" i `ksef_invoice` dla KSeF → zakup.
   */
  forInvoice(relationType, invoiceId) {
    return axios.get(`${ apiPaths.API }/documents/for-invoice/${ relationType }/${ invoiceId }`);
  },
  forVehicle(vehicleOriginal) {
    return axios.get(`${ apiPaths.BOOKKEEPING }/vehicles/${ vehicleOriginal }/documents`);
  },
  /**
   * Dokumenty w kontekście środka trwałego — adresowane stabilnym `original`, nie `id` wersji.
   */
  forFixedAsset(fixedAssetOriginal) {
    return axios.get(`${ apiPaths.BOOKKEEPING }/fixed-assets/${ fixedAssetOriginal }/documents`);
  },
};
